
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Calendar, User, Building, MessageSquare, Camera, X } from "lucide-react"
import { useState } from "react"

interface OcorrenciaDetalhes {
  id: string
  titulo: string
  descricao: string
  categoria: string
  prioridade: string
  status: string
  unidade: string
  relator: string
  dataAbertura: string
  dataUltimaAtualizacao?: string
  justificativa?: string
  fotos?: string[]
}

interface DetalhesOcorrenciaModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ocorrencia: OcorrenciaDetalhes | null
  canEdit?: boolean
}

const DetalhesOcorrenciaModal = ({ open, onOpenChange, ocorrencia, canEdit = false }: DetalhesOcorrenciaModalProps) => {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null)

  if (!ocorrencia) return null

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Resolvida":
        return "bg-green-100 text-green-800 border-green-200"
      case "Em Andamento":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "Aberta":
        return "bg-red-100 text-red-800 border-red-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Urgente":
        return "bg-red-100 text-red-800 border-red-200"
      case "Alta":
        return "bg-orange-100 text-orange-800 border-orange-200"
      case "Média":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "Baixa":
        return "bg-green-100 text-green-800 border-green-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle className="text-xl font-bold text-gray-900 mb-2">
                  {ocorrencia.titulo}
                </DialogTitle>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={getStatusColor(ocorrencia.status)}>
                    {ocorrencia.status}
                  </Badge>
                  <Badge variant="outline" className={getPriorityColor(ocorrencia.prioridade)}>
                    {ocorrencia.prioridade}
                  </Badge>
                  <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                    {ocorrencia.categoria}
                  </Badge>
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Descrição</h3>
              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                {ocorrencia.descricao}
              </p>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Reportado por</p>
                    <p className="text-sm text-gray-600">{ocorrencia.relator}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Unidade</p>
                    <p className="text-sm text-gray-600">{ocorrencia.unidade}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Data de Abertura</p>
                    <p className="text-sm text-gray-600">
                      {new Date(ocorrencia.dataAbertura).toLocaleDateString('pt-BR')} às{' '}
                      {new Date(ocorrencia.dataAbertura).toLocaleTimeString('pt-BR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
                {ocorrencia.dataUltimaAtualizacao && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Última Atualização</p>
                      <p className="text-sm text-gray-600">
                        {new Date(ocorrencia.dataUltimaAtualizacao).toLocaleDateString('pt-BR')} às{' '}
                        {new Date(ocorrencia.dataUltimaAtualizacao).toLocaleTimeString('pt-BR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {ocorrencia.fotos && ocorrencia.fotos.length > 0 && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <Camera className="h-4 w-4 text-gray-500" />
                    <h3 className="font-semibold text-gray-900">Fotos</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {ocorrencia.fotos.map((foto, index) => (
                      <div key={index} className="relative">
                        <img
                          src={foto}
                          alt={`Foto ${index + 1} da ocorrência`}
                          className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setSelectedPhoto(foto)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {ocorrencia.justificativa && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <MessageSquare className="h-4 w-4 text-gray-500" />
                    <h3 className="font-semibold text-gray-900">Observações</h3>
                  </div>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                    {ocorrencia.justificativa}
                  </p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para visualizar foto em tamanho maior */}
      {selectedPhoto && (
        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] p-0">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2 z-10"
                onClick={() => setSelectedPhoto(null)}
              >
                <X className="h-4 w-4" />
              </Button>
              <img
                src={selectedPhoto}
                alt="Foto da ocorrência"
                className="w-full h-auto max-h-[85vh] object-contain"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default DetalhesOcorrenciaModal
